import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { children as childrenAPI } from "@/lib/endpoints";
import { useApplicant } from "@/hooks/useApplicant";
import { useFormResponses } from "@/hooks/useFormResponses";
import {
  combineFormError,
  getFieldErrors,
  getFormError,
  runUpserts,
} from "@/lib/validation-errors";
import { useNestedList } from "@/hooks/useNestedList";
import { Field, Select, TextInput } from "@/components/Field";
import type { ChildResponse, CreateChildRequest } from "@/lib/api-types";

// USCIS expects one of these three relationship values per child.
// The mapping (n400_2025_01_20.go) reads form_response keys
// `child_{N}_relationship` where N is the 1-based slot in the
// children list. Keep these labels in sync with the mapper's switch.
const RELATIONSHIP_OPTIONS: { value: string; label: string }[] = [
  { value: "biological", label: "Biological son or daughter" },
  { value: "step", label: "Stepchild" },
  { value: "adopted", label: "Legally adopted son or daughter" },
];

const relationshipKey = (slot1Based: number) => `child_${slot1Based}_relationship`;

/**
 * Step 6 — Children (Part 8 of N-400).
 * USCIS wants every child, even adult or stepchildren — anywhere in the world.
 */
export function StepChildren() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { applicant, isLoading: applicantLoading } = useApplicant(id);
  const list = useNestedList<ChildResponse, CreateChildRequest>(
    "children",
    childrenAPI,
    id,
    applicant?.id,
  );

  const [editing, setEditing] = useState<ChildResponse | "new" | null>(null);

  // Per-child relationship state, keyed by child UUID so reorders /
  // edits don't shuffle the values among kids. We read the existing
  // slot-keyed responses by current list order on mount, then write
  // them back the same way on Save & continue.
  const fr = useFormResponses(id);
  const [relByChildId, setRelByChildId] = useState<Record<string, string>>({});
  const [validationErr, setValidationErr] = useState<string | null>(null);
  const [saveErrors, setSaveErrors] = useState<unknown[]>([]);

  useEffect(() => {
    if (fr.isLoading || list.isLoading) return;
    const next: Record<string, string> = {};
    list.items.forEach((c, i) => {
      next[c.id] = fr.getString(relationshipKey(i + 1));
    });
    setRelByChildId(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fr.isLoading, list.isLoading, list.items.length]);

  if (applicantLoading || list.isLoading) {
    return <div className="text-slate-500">Loading…</div>;
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Children</h1>
        <p className="text-sm text-slate-500 mt-1">
          List every child of yours — adult or minor, biological, adopted, or
          stepchild — regardless of where they live.
        </p>
      </header>

      <ul className="space-y-3">
        {list.items.map((c) => (
          <li
            key={c.id}
            className="bg-white rounded-xl border border-slate-200 p-4 flex items-start justify-between"
          >
            <div className="text-sm">
              <div className="font-medium">{c.full_name}</div>
              <div className="text-slate-500">
                Born {c.date_of_birth} · {c.country_of_birth}
              </div>
              {c.lives_with_applicant != null && (
                <div className="text-xs text-slate-400 mt-1">
                  {c.lives_with_applicant ? "Lives with you" : "Does not live with you"}
                </div>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setEditing(c)}
                className="text-sm text-slate-600 hover:text-slate-900"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => {
                  if (confirm("Delete this child?")) list.remove.mutate(c.id);
                }}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>

      {!editing && (
        <button
          type="button"
          onClick={() => setEditing("new")}
          className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          + Add child
        </button>
      )}

      {editing && (
        <ChildForm
          initial={editing === "new" ? null : editing}
          onCancel={() => setEditing(null)}
          onSubmit={async (req) => {
            if (editing === "new") await list.create.mutateAsync(req);
            else await list.update.mutateAsync({ id: editing.id, req });
            setEditing(null);
          }}
          saving={list.create.isPending || list.update.isPending}
          error={editing === "new" ? list.create.error : list.update.error}
        />
      )}

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setValidationErr(null);
          setSaveErrors([]);
          const missing = list.items.find((c) => !relByChildId[c.id]);
          if (missing) {
            setValidationErr(`Please pick a relationship for ${missing.full_name ?? "(unnamed child)"}.`);
            return;
          }
          const upserts: Promise<unknown>[] = [];
          list.items.forEach((c, i) => {
            const v = relByChildId[c.id];
            if (v) {
              upserts.push(fr.upsert.mutateAsync({
                fieldKey: relationshipKey(i + 1), value: v,
              }));
            }
          });
          const errors = await runUpserts(upserts);
          if (errors.length > 0) {
            setSaveErrors(errors);
            return;
          }
          navigate(`/cases/${id}/wizard/criminal`);
        }}
        className="space-y-6"
      >
      {list.items.length > 0 && (
        <fieldset className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <legend className="font-medium text-slate-900 px-1">
            Your relationship to each child
          </legend>
          <p className="text-sm text-slate-500">
            USCIS asks you to specify whether each child is biological,
            adopted, or a stepchild.
          </p>
          {list.items.map((c) => (
            <Field key={c.id} label={c.full_name ?? "(unnamed child)"}>
              <Select
                value={relByChildId[c.id] ?? ""}
                onChange={(e) =>
                  setRelByChildId((s) => ({ ...s, [c.id]: e.target.value }))
                }
              >
                <option value="">Select…</option>
                {RELATIONSHIP_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>
          ))}
        </fieldset>
      )}

      {validationErr && (
        <p className="text-sm text-red-600">{validationErr}</p>
      )}
      {combineFormError(saveErrors) && (
        <p className="text-sm text-red-600">
          Could not save: {combineFormError(saveErrors)}
        </p>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
        <Link to={`/cases/${id}/wizard/marriage`} className="text-sm text-slate-600 hover:text-slate-900">
          ← Marital history
        </Link>
        <button
          type="submit"
          disabled={fr.upsert.isPending}
          className="rounded-xl bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-slate-800 disabled:bg-slate-300"
        >
          {fr.upsert.isPending ? "Saving…" : "Save & continue"}
        </button>
      </div>
      </form>
    </div>
  );
}

function ChildForm({
  initial,
  onCancel,
  onSubmit,
  saving,
  error,
}: {
  initial: ChildResponse | null;
  onCancel: () => void;
  onSubmit: (req: CreateChildRequest) => Promise<void>;
  saving: boolean;
  error?: unknown;
}) {
  const [form, setForm] = useState<CreateChildRequest>({
    full_name: initial?.full_name ?? "",
    date_of_birth: initial?.date_of_birth ?? "",
    country_of_birth: initial?.country_of_birth ?? "",
    lives_with_applicant: initial?.lives_with_applicant ?? null,
    current_immigration_status: initial?.current_immigration_status ?? "",
  });

  const fieldErrors = getFieldErrors(error);
  const formError = getFormError(error);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const clean: CreateChildRequest = { ...form };
        (Object.keys(clean) as (keyof CreateChildRequest)[]).forEach((k) => {
          if (clean[k] === "") (clean as Record<string, unknown>)[k] = undefined;
        });
        void onSubmit(clean);
      }}
      className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4"
    >
      <h3 className="font-semibold">{initial ? "Edit child" : "Add child"}</h3>

      <Field
        label="Full legal name"
        required
        error={fieldErrors?.["full_name"]?.message}
      >
        <TextInput
          value={form.full_name ?? ""}
          onChange={(e) => setForm((s) => ({ ...s, full_name: e.target.value }))}
        />
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          label="Date of birth"
          required
          error={fieldErrors?.["date_of_birth"]?.message}
        >
          <TextInput
            type="date"
            value={form.date_of_birth ?? ""}
            onChange={(e) => setForm((s) => ({ ...s, date_of_birth: e.target.value }))}
          />
        </Field>
        <Field
          label="Country of birth"
          required
          error={fieldErrors?.["country_of_birth"]?.message}
        >
          <TextInput
            value={form.country_of_birth ?? ""}
            onChange={(e) => setForm((s) => ({ ...s, country_of_birth: e.target.value }))}
          />
        </Field>
      </div>

      <Field
        label="Current immigration status"
        hint="U.S. citizen, LPR, etc."
        error={fieldErrors?.["current_immigration_status"]?.message}
      >
        <TextInput
          value={form.current_immigration_status ?? ""}
          onChange={(e) =>
            setForm((s) => ({ ...s, current_immigration_status: e.target.value }))
          }
        />
      </Field>

      <fieldset className="text-sm">
        <legend className="font-medium">Does this child live with you?</legend>
        <div className="flex gap-3 mt-2">
          {[
            { v: true, label: "Yes" },
            { v: false, label: "No" },
          ].map(({ v, label }) => (
            <button
              key={label}
              type="button"
              onClick={() => setForm((s) => ({ ...s, lives_with_applicant: v }))}
              className={
                "px-4 py-1.5 rounded-lg border text-sm " +
                (form.lives_with_applicant === v
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-300")
              }
            >
              {label}
            </button>
          ))}
        </div>
      </fieldset>

      {formError && (
        <p className="text-sm text-red-600">Could not save: {formError}</p>
      )}
      {fieldErrors && (
        <p className="text-xs text-red-600">
          Some fields above need attention before continuing.
        </p>
      )}
      <div className="flex items-center justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="text-sm text-slate-600 hover:text-slate-900">
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="rounded-xl bg-slate-900 text-white px-4 py-2 text-sm font-medium hover:bg-slate-800 disabled:bg-slate-300"
        >
          {saving ? "Saving…" : initial ? "Save changes" : "Add"}
        </button>
      </div>
    </form>
  );
}
